import React, { useEffect, useState } from "react";
import { Alert, Badge, Box, Button, Card, Grid, Icon, Spinner, Text } from "~/components-shared";
import styled from "styled-components";
import { GraphNodeTypeEnum } from "~/types/enums";
import { genericColor } from "~/lib/resolvers";
import api from "~/lib/api";
import { handleError } from "~/lib/helpers";
import lists from "~/lib/lists";
import { edgeColors } from "~/views/private/KnowledgeGraphCanvas/KnowledgeGraphCanvas";
const Actions = styled(Box)`
  position: absolute;
  top: 1em;
  right: 1em;
`;
const StyledCard = styled(Card)`
  position: absolute;
  z-index: 10;
  top: 1em;
  right: 1em;
  backdrop-filter: blur(5px);
  background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.primary;
}}95;
  box-shadow: 0 0 60px 0 rgba(0, 0, 0, 0.3);
  & > div {
    overflow: hidden;
  }
`;
const Chip = styled.div`
  height: 5px;
  width: 8%;
  border-radius: 3px;
  background-color: ${_ref2 => {
  let {
    theme,
    $color
  } = _ref2;
  return genericColor(theme, $color);
}};
`;
const StyledBox = styled(Box)`
  flex-grow: 1;
  overflow: auto;
  max-height: 100%;
`;
const GraphNodeDetails = _ref3 => {
  let {
    minEdges,
    maxEdges,
    node,
    onClose,
    onNodeSelect,
    onNodeDelete
  } = _ref3;
  const [nodeSiblings, setNodeSiblings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(undefined);
  useEffect(() => {
    if (node) {
      setLoading(true);
      setError(undefined);
      api.fetchGraphNodeData(node.id).then(data => {
        setNodeSiblings(data);
      }).catch(e => setError(handleError(e))).finally(() => setLoading(false));
    }
  }, [node]);
  if (!node) return null;
  const getIcon = type => {
    switch (type) {
      case GraphNodeTypeEnum.TREND:
        return "trendingUp";
      case GraphNodeTypeEnum.TOPIC:
        return "interests";
      case GraphNodeTypeEnum.ENTITY:
        return "sell";
      case GraphNodeTypeEnum.DOCUMENT:
        return "document";
      case GraphNodeTypeEnum.THEME:
        return "chart";
      default:
        return "sell";
    }
  };
  const range = maxEdges - minEdges;
  const intensity = Math.round(node.totalCount / range * 100 / 10) || 1;
  return /*#__PURE__*/React.createElement(StyledCard, {
    s: {
      p: 4,
      mt: 16,
      mr: 16
    },
    width: "360px",
    maxHeight: "75vh",
    color: "backgroundDark",
    shadow: true
  }, /*#__PURE__*/React.createElement(Box, {
    s: {
      mb: 4
    }
  }, /*#__PURE__*/React.createElement(Box, {
    gap: 3,
    direction: "row",
    wrap: "nowrap"
  }, /*#__PURE__*/React.createElement(Badge, {
    color: edgeColors(node.subdomain, node.type),
    s: {
      p: 3
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 6,
    color: "#ffffff",
    src: getIcon(node.type)
  })), /*#__PURE__*/React.createElement(Box, {
    s: {
      pr: 5
    },
    direction: "column"
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    weight: "bold",
    style: {
      overflowWrap: "anywhere"
    },
    s: {
      pr: 4
    }
  }, node.name), /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    weight: "regular",
    color: "textSecondary"
  }, node.data.subtype ? `${lists.visualize.nodeSubtypes.names[node.data.subtype]} ` : "", lists.visualize.nodeTypes.names[node.type]))), /*#__PURE__*/React.createElement(Actions, {
    direction: "column",
    gap: 2
  }, /*#__PURE__*/React.createElement(Button, {
    color: "translucent",
    s: {
      p: 2
    },
    onClick: onClose,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": "Close Details"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "cross",
    display: "block"
  })), /*#__PURE__*/React.createElement(Button, {
    color: "translucentDanger",
    s: {
      p: 2
    },
    onClick: () => onNodeDelete(node),
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": "Delete Node"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "bin",
    display: "block"
  })))), /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    gap: 4,
    style: {
      flexGrow: 1,
      maxHeight: "100%",
      overflow: "hidden",
      flexFlow: "column"
    }
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "column"
  }, /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 3,
    weight: "bold",
    s: {
      mb: 1
    }
  }, "Subdomain"), /*#__PURE__*/React.createElement(Box, {
    gap: 2
  }, node.subdomain ? node.subdomain.split(",").map(item => /*#__PURE__*/React.createElement(Badge, {
    color: "translucent",
    s: {
      py: 0.5
    }
  }, lists.visualize.nodeSubdomains.names[item])) : /*#__PURE__*/React.createElement(Badge, {
    color: "translucent",
    s: {
      py: 0.5
    }
  }, "No subdomain"))), /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 3,
    weight: "bold",
    s: {
      mb: 1
    }
  }, "Occurrence"), /*#__PURE__*/React.createElement(Box, {
    gap: 1,
    direction: "row",
    s: {
      mb: 1
    },
    fullWidth: true
  }, Array.from({
    length: 10
  }).map((_, i) => /*#__PURE__*/React.createElement(Chip, {
    key: i,
    $color: i < intensity ? edgeColors(node.subdomain, node.type) : "translucent"
  }))), /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary"
  }, /*#__PURE__*/React.createElement("b", null, node.totalCount), " in ", minEdges, " - ", maxEdges, " range")), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), loading ? /*#__PURE__*/React.createElement(Spinner, {
    color: "textSecondary",
    scale: 10,
    center: true
  }) : !!nodeSiblings && /*#__PURE__*/React.createElement(StyledBox, {
    gap: 2,
    direction: "row",
    s: {
      pr: 2
    }
  }, !!nodeSiblings.sourceNodes.length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3.5,
    weight: "semiBold",
    display: "block"
  }, "Sources"), nodeSiblings.sourceNodes.map(item => /*#__PURE__*/React.createElement(Card, {
    color: "translucent",
    key: item.id,
    s: {
      p: 2
    },
    onClick: () => onNodeSelect(item.node),
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    template: "auto 1fr auto",
    gap: 2
  }, /*#__PURE__*/React.createElement(Badge, {
    color: edgeColors(item.node.subdomain, item.node.type),
    s: {
      p: 2
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    color: "#ffffff",
    src: getIcon(item.node.type)
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    fontSize: 3.5,
    display: "block"
  }, item.node.name), /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary"
  }, lists.visualize.nodeTypes.names[item.node.type], item.node.subdomain ? ` • ${item.node.subdomain.split(",").map(s => lists.visualize.nodeSubdomains.names[s]).join(", ")}` : "")), /*#__PURE__*/React.createElement(Badge, {
    color: "translucent",
    s: {
      px: 2,
      py: 0.5
    }
  }, lists.visualize.nodeTypes.names[item.type]))))), !!nodeSiblings.targetNodes.length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3.5,
    weight: "semiBold",
    display: "block"
  }, "Targets"), nodeSiblings.targetNodes.map(item => /*#__PURE__*/React.createElement(Card, {
    color: "translucent",
    key: item.id,
    s: {
      p: 2
    },
    onClick: () => onNodeSelect(item.node),
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    template: "auto 1fr auto",
    gap: 2
  }, /*#__PURE__*/React.createElement(Badge, {
    color: edgeColors(item.node.subdomain, item.node.type),
    s: {
      p: 2
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    color: "#ffffff",
    src: getIcon(item.node.type)
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    fontSize: 3.5,
    display: "block"
  }, item.node.name), /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary"
  }, item.node.data.subtype ? `${lists.visualize.nodeSubtypes.names[item.node.data.subtype]} ` : "", lists.visualize.nodeTypes.names[item.node.type], item.node.subdomain ? ` • ${item.node.subdomain.split(",").map(s => lists.visualize.nodeSubdomains.names[s]).join(", ")}` : "")), /*#__PURE__*/React.createElement(Badge, {
    color: "translucent",
    s: {
      px: 2,
      py: 0.5
    }
  }, lists.visualize.nodeTypes.names[item.type]))))))));
};
export default GraphNodeDetails;